import React, { useState, useCallback, useRef, useEffect } from "react"
import styled from "styled-components"
import { useLocation } from "react-router-dom"
import { FilterValues } from "../components/filters/FilterSection"
import InfluencerList from "../components/influencers/InfluencerList"
import Container from "../components/common/Container"
import { Influencer } from "../types/influencer"
import { DataService } from "../services"

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`

const Header = styled.header`
  background: ${({ theme }) => theme.colors.backgroundDark};
  padding: ${({ theme }) => theme.spacing[6]} 0
    ${({ theme }) => theme.spacing[5]};
  box-shadow: 0 4px 20px rgba(12, 21, 40, 0.2);
  position: relative;
  overflow: hidden;

  /* Add subtle gradient overlay */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(67, 97, 238, 0.1) 0%,
      rgba(114, 9, 183, 0.05) 100%
    );
    z-index: 0;
  }

  /* Add subtle pattern overlay */
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle at 25% 25%,
      rgba(255, 255, 255, 0.05) 1%,
      transparent 6%
    );
    background-size: 50px 50px;
    z-index: 0;
  }
`

const HeaderContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  cursor: pointer;
`

const LogoLink = styled.a`
  display: flex;
  align-items: center;
`

const HeaderLogoImage = styled.img`
  height: 45px;
  margin-right: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 65px;
  }
`

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2.5rem 1rem 1rem;
  text-align: center;
  position: relative;
  z-index: 2;

  h1 {
    color: white;
    font-size: 2.2rem;
    font-weight: bold;
    margin-bottom: 1.5rem;
    text-align: center;
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      text-align: left;
      font-size: 1.8rem;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    h1 {
      font-size: 2.5rem;
      margin-bottom: 1.75rem;
    }
  }
`

const ContentWrapper = styled.div`
  flex-grow: 1;
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  padding: ${({ theme }) => theme.spacing[4]};
`

const ResultsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const InfluencerResultsPage: React.FC = () => {
  const location = useLocation()
  const ignoreFetchResultsRef = useRef(false)

  const [selectedFilters, setSelectedFilters] = useState<FilterValues>({
    industry: "",
    country: "",
    budget: "",
    businessEmail: "",
    companyName: "",
    phoneNumber: "",
  })

  const [influencers, setInfluencers] = useState<Influencer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasSubmittedEmail] = useState<boolean>(true)
  const [showResults, setShowResults] = useState<boolean>(false)

  // Helper to get budget range for service
  const getBudgetRangeForService = (
    budgetKey: string
  ): { minBudget?: number; maxBudget?: number } => {
    const budgetMap: Record<string, { min: number; max: number }> = {
      "0-3000": { min: 0, max: 2999 },
      "0-4999": { min: 0, max: 4999 },
      "3000-5000": { min: 3000, max: 4999 },
      "5000-10000": { min: 5000, max: 9999 },
      "10000+": { min: 10000, max: Number.MAX_SAFE_INTEGER },
      "5000-15000": { min: 5000, max: 14999 },
      "15000-25000": { min: 15000, max: 24999 },
      "25000-50000": { min: 25000, max: 49999 },
      "50000+": { min: 50000, max: Number.MAX_SAFE_INTEGER },
    }
    const range = budgetMap[budgetKey]
    if (range) {
      return { minBudget: range.min, maxBudget: range.max }
    }
    return {}
  }

  const fetchInfluencers = useCallback(async (filterValues: FilterValues) => {
    setIsLoading(true)

    const budgetRangeParams = filterValues.budget
      ? getBudgetRangeForService(filterValues.budget)
      : {}

    const paramsForService = {
      industry: filterValues.industry,
      country: filterValues.country,
      businessEmail: filterValues.businessEmail,
      ...(budgetRangeParams.minBudget !== undefined && {
        minBudget: budgetRangeParams.minBudget,
      }),
      ...(budgetRangeParams.maxBudget !== undefined && {
        maxBudget: budgetRangeParams.maxBudget,
      }),
    }

    try {
      const response = await DataService.getInfluencers(paramsForService as any)

      if (ignoreFetchResultsRef.current) {
        setIsLoading(false)
        return
      }

      setInfluencers(response.influencers)
      setShowResults(true)
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleChangeFilters = () => {
    ignoreFetchResultsRef.current = true
    window.location.href = window.location.pathname.replace(
      "/influencer-results",
      "/influencer-research-tool"
    )
  }

  // Check for query parameters on mount and auto-load results
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    // Check if we have filter parameters in the URL
    const hasFilterParams =
      searchParams.has("industry") ||
      searchParams.has("country") ||
      searchParams.has("budget") ||
      searchParams.has("businessEmail")

    if (hasFilterParams) {
      const filterValues: FilterValues = {
        industry: searchParams.get("industry") || "",
        country: searchParams.get("country") || "",
        budget: searchParams.get("budget") || "",
        businessEmail: searchParams.get("businessEmail") || "",
        companyName: searchParams.get("companyName") || "",
        phoneNumber: searchParams.get("phoneNumber") || "",
      }

      setSelectedFilters(filterValues)
      fetchInfluencers(filterValues)
    }
  }, [location.search, fetchInfluencers])

  const generateSignupUrl = useCallback(
    (filterValues: FilterValues): string => {
      const params = new URLSearchParams()

      if (filterValues.industry) {
        if (filterValues.industry === "other") {
          params.set("source_industry", "not_listed")
        } else {
          params.set("source_industry", filterValues.industry)
        }
      }

      if (filterValues.country) {
        if (filterValues.country === "other") {
          params.set("source_country", "not_listed")
        } else {
          params.set("source_country", filterValues.country)
        }
      }

      if (filterValues.budget) {
        params.set("source_budget", filterValues.budget)
      }

      if (filterValues.businessEmail) {
        params.set("email", filterValues.businessEmail)
      }

      // Add current URL parameters (like UTM params)
      const currentParams = new URLSearchParams(location.search)
      currentParams.forEach((value, key) => {
        if (!params.has(key)) {
          params.set(key, value)
        }
      })

      return `https://business.hypefy.ai/auth/signup${
        params.toString() ? "?" + params.toString() : ""
      }`
    },
    [location.search]
  )

  const getSignupUrl = () => {
    return generateSignupUrl(selectedFilters)
  }

  return (
    <AppWrapper>
      <Header>
        <HeaderContainer maxWidth="1400px">
          <LogoContainer>
            <LogoLink
              href={`https://hypefy.ai?referrer=freemium-influencer-list${
                location.search ? "&" + location.search.substring(1) : ""
              }`}
              target="_blank"
            >
              <HeaderLogoImage src="/images/logo_white.png" alt="Hypefy Logo" />
            </LogoLink>
          </LogoContainer>
        </HeaderContainer>

        <HeaderContent>
          <h1>Your Personalized Influencer List</h1>
        </HeaderContent>
      </Header>

      <ContentWrapper>
        <ResultsContainer>
          {showResults && (
            <InfluencerList
              influencers={influencers}
              isLoading={isLoading}
              hasSubmittedEmail={hasSubmittedEmail}
              currentFilters={selectedFilters}
              onChangeFilters={handleChangeFilters}
              signupUrl={getSignupUrl()}
              showCTA={false}
            />
          )}
        </ResultsContainer>
      </ContentWrapper>
    </AppWrapper>
  )
}

export default InfluencerResultsPage
